/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ContentType } from '../../../core/enums/ContentType';
import { TransitionScreenType } from '../../../core/enums/TransitionScreenType';
import { NdvrInteractor } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import {
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../../core/navigation';
import { Screen } from '../../../core/navigation/Screen';
import { ScreenController } from '../../../core/navigation/ScreenController';
import { TScreen } from '../../../core/navigation/types';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { TProgramScreenState } from '../../../core/types/TProgramScreenState';
import { BiaxialActionWidgetHelper } from '../../../views/ProgramScreen/BiaxialActionWidget/BiaxialActionWidgetHelper';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import { resetMyShows } from '../../myshows/myshows.actions';
import { MyShowsListGenerator } from '../../myshows/MyShowsListGenerator';
import {
    setCurrentVerticalHighlightIndexProgramScreenAction,
    setHorizontalOptionsProgramScreenAction,
} from '../../programscreen/programscreen.actions';
import { showCollectionScreen, showProgramScreen } from '../screens.reducers';

const preCheckForProgramScreenTransition = async (id: string, contentType: ContentType) => {
    try {
        const ndvrServices = new NdvrInteractor();
        switch (contentType) {
            case ContentType.RECORDING_ID:
                await ndvrServices.getContentScreenDetailsRecordingActions({
                    recordingId: id,
                });
                break;
            case ContentType.CONTENT_ID:
                await ndvrServices.getContentScreenDetailsContentActions({
                    contentId: id,
                });
                break;
            case ContentType.OFFER_ID:
                await ndvrServices.getContentScreenDetailsOfferActions({
                    offerId: id,
                });
                break;
            default:
        }
    } catch (_error) {
        throw new Error(Locale.getString(LocaleKeys.MY_SHOWS_TRANSITION_ERROR_MESSAGE));
    }
};

const preCheckForCollectionScreenTransition = async (id: string) => {
    try {
        const ndvrServices = new NdvrInteractor();
        await ndvrServices.getUCScreenDetailsActions({
            unifiedCollectionId: id,
        });
    } catch (error: any) {
        throw new Error(Locale.getString(LocaleKeys.MY_SHOWS_TRANSITION_ERROR_MESSAGE));
    }
};

export const collectionScreenTransition =
    (unifiedCollectionId: string, transitionScreen: TransitionScreenType, callback?: Function) =>
    async (dispatch: Function) => {
        try {
            if (!KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().startKeyIgnoring();

            await preCheckForCollectionScreenTransition(unifiedCollectionId);
            const collectionSharedPreferenceEditor = new SharedPreferences(
                SharedPrefConstants.COLLECTION_INFO_PREF_KEY,
            ).edit();
            collectionSharedPreferenceEditor.putJson(SharedPrefConstants.COLLECTION_INFO, {
                unifiedCollectionId,
                transitionScreen,
            } as unknown as JSON);
            collectionSharedPreferenceEditor.commit();
            dispatch(showCollectionScreen());
            MyShowsListGenerator.reset();
            dispatch(resetMyShows());

            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
        } catch (error: any) {
            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
            if (callback)
                dispatch(callback({ message: error.message, iconType: WhisperTypes.NO_ICON }));
        }
    };

export const programScreenTransition =
    (id: string, contentType: ContentType, callback?: Function) =>
    async (dispatch: Function, getState: Function) => {
        if (!KeyManager.getInstance().isKeyIgnoring()) KeyManager.getInstance().startKeyIgnoring();
        try {
            await preCheckForProgramScreenTransition(id, contentType);
            const newScreen = new Screen<TProgramScreenState & TScreen>(
                `ProgramScreen-${id}-${Date.now()}`,
            );
            const currScreen = ScreenController.getInstance().getScreen();
            const currScreenProperties = ScreenController.getInstance()
                .getScreen()
                .getProperties()!;
            currScreen.setProperties({
                ...currScreenProperties,
                horizontalIndex: getState().programScreen.currentHighlightIndex,
                verticalIndex: getState().programScreen.currentVerticalHighlightIndex,
                currentImageIndex: BiaxialActionWidgetHelper.getCurrentImageIndex(),
            });
            const screenProperties: TProgramScreenState & TScreen = {
                url: `${LocationUrls.PROGRAM_SCREEN}/${Date.now()}`,
                screenController: ScreenControllerTypes.PROGRAM_SCREEN,
                taskController: ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS,
                contentType,
                horizontalIndex: 0,
                id,
                verticalIndex: 0,
                currentImageIndex: 0,
            };
            dispatch(setCurrentVerticalHighlightIndexProgramScreenAction(0));
            newScreen.setProperties(screenProperties);
            ScreenController.getInstance().push(newScreen);
            dispatch(setHorizontalOptionsProgramScreenAction([]));
            dispatch(showProgramScreen());
            MyShowsListGenerator.reset();
            dispatch(resetMyShows());
        } catch (error: any) {
            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
            if (callback)
                dispatch(callback({ message: error.message, iconType: WhisperTypes.NO_ICON }));
        }
    };
